import {GameStateEnums} from '../../../../../shared/GameEnums'
import {GameColor} from '../../GameColor'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGameManager} from '../UIGameManager'
import {PIXI} from '@colonist/pixi'
import {GameAction} from '@colonist/game-base-types'
import {TextView} from '../../ui_canvas_engine/TextView'
import {GameFont} from '../../GameFont'
import {TranslationObject} from '@colonist/i18n'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import {UIGameFlickerAnimation} from '../../ui_canvas_engine/animation/UIGameFlickerAnimation'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {Sprites} from '../UIGameImageDictionary'
import {EnumUtils} from '@colonist/utils'
import {TradeValidator} from '@colonist/game-mechanic-validators'
import {CardEnum, TradeId, TradeResponseStatus} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'

export class UIGameBeginnerModeGuideBubble implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager
    private readonly parentContainer: PIXI.Container

    private guideBubbleView: SectionBgAdaptiveView
    private guideBubbleViewPointer: PIXI.Graphics
    private guideTextView: TextView

    currentBeginnerModeAction: BeginnerModeActionState
    private shownTranslationObject: TranslationObject

    private backgroundClickAmount: number = 0
    private readonly BACKGROUND_CLICK_AMOUNT_TO_SHOW_GUIDE: number = 2

    private ongoingTradeExists: boolean = false
    private receivingResourceName?: string | undefined

    constructor(uiGameManager: UIGameManager, viewToPlaceNear: PIXI.Container) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.parentContainer = viewToPlaceNear.parent
        this.drawBeginnerModeGuideBubble(viewToPlaceNear)
        this.hide()
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.NewTradeOfferReceived: this.onTradeOfferReceived(data); break
            case UIGameEventEnums.RemoveTradeOffer: this.ongoingTradeExists = false; break
            case UIGameEventEnums.BeginnerModeInteractWithGuide: this.onInteractWithGuide(); break
            case UIGameEventEnums.BeginnerModeForceOpenGuideBubble: this.forceOpenOrHighlightGuideBubble(data); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            case UIGameEventEnums.PlayerControllerStateUpdated: if(!this.isWelcomeAction()) this.hide(); break
            default: break
        }
    }

    private drawBeginnerModeGuideBubble(viewToPlaceNear: PIXI.Container) {
        const itemSize = this.uiGameManager.canvasController.itemSize

        this.drawBackground(viewToPlaceNear, itemSize)
        this.drawBeginnerModeGuideBubblePointer(itemSize)
    }

    private drawBackground(viewToPlaceNear: PIXI.Container, itemSize: number) {
        const guideViewX = viewToPlaceNear.x
        const guideViewY = viewToPlaceNear.y - itemSize * 1.4

        this.guideBubbleView = new SectionBgAdaptiveView()
        this.guideBubbleView.x = guideViewX
        this.guideBubbleView.y = guideViewY
        this.guideBubbleView.zIndex = this.uiGameManager.mapController.mapView.layers.robber
    }

    private drawBeginnerModeGuideBubblePointer(itemSize: number) {
        const margin = this.uiGameManager.canvasController.margin / 3
        const outlineAlpha = 0.2

        const triangleX1 = itemSize * 0.4
        const triangleX2 = itemSize * 0.7
        const triangleY1 = this.guideBubbleView.height - margin
        const triangleY2 = triangleY1 + itemSize * 0.2

        this.guideBubbleViewPointer = new PIXI.Graphics()
        this.guideBubbleViewPointer.beginFill(GameColor.getColorHexValue(GameColor.Color.Gray0))
        this.guideBubbleViewPointer.lineStyle(margin, undefined, outlineAlpha)
        this.guideBubbleViewPointer.moveTo(triangleX1, triangleY1)
        this.guideBubbleViewPointer.lineTo(triangleX2, triangleY1)
        this.guideBubbleViewPointer.lineTo(triangleX1, triangleY2)
        this.guideBubbleViewPointer.lineTo(triangleX1, triangleY1)
        this.guideBubbleViewPointer.endFill()

        this.parentContainer.addChild(this.guideBubbleViewPointer)

        this.guideBubbleViewPointer.zIndex = this.uiGameManager.mapController.mapView.layers.robber
        this.guideBubbleViewPointer.x = this.guideBubbleView.x
        this.guideBubbleViewPointer.y = this.guideBubbleView.y + itemSize * 1.2
    }

    private onInteractWithGuide() {
        if(this.isWelcomeAction()) return this.endWelcomeOnInteraction()

        this.backgroundClickAmount += 1
        if(this.backgroundClickAmount < this.BACKGROUND_CLICK_AMOUNT_TO_SHOW_GUIDE) return

        this.forceOpenOrHighlightGuideBubble()
    }

    private isWelcomeAction(): boolean {
        return this.currentBeginnerModeAction?.type == GameAction.SkippedWelcome
    }

    private endWelcomeOnInteraction() {
        this.hide()
    }

    private forceOpenOrHighlightGuideBubble(forcedBubbleText?: TranslationObject) {
        if(forcedBubbleText != undefined) this.currentBeginnerModeAction.guideText = forcedBubbleText
        if(this.shownTranslationObject != this.currentBeginnerModeAction.guideText) this.updateGuideBubbleText()

        const isGuideBubbleVisible = this.guideBubbleView.parent != undefined
        isGuideBubbleVisible ? this.animate() : this.show()
    }

    private onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        this.currentBeginnerModeAction = data
        this.backgroundClickAmount = 0

        if(!this.isWelcomeAction()) return this.hide()

        this.updateGuideBubbleText()
        this.show()
    }

    private drawGuideBubbleTextView() {
        const itemSize = this.uiGameManager.canvasController.itemSize
        this.guideTextView?.destroy()

        const isMobile = UIUtilsEjsVariables.isUserOnMobileAppOrWeb && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI && !UIUtilsEjsVariables.isUserOnTabletApp
        const width = itemSize * (isMobile ? 4.2 : 1.45)
        const height = itemSize * (isMobile ? 4 : 1.1)
        const fontSize = isMobile ? GameFont.Size.XXL : GameFont.Size.L
        this.guideTextView = new TextView(this.currentBeginnerModeAction.guideText, 0, 0, width, height, this.guideBubbleView, fontSize, GameColor.getColor(GameColor.Color.White))
    }

    private drawBackgroundAroundText() {
        const itemSize = this.uiGameManager.canvasController.itemSize
        const horizontalBuffer = itemSize * 0.4
        const verticalBuffer = itemSize * 0.2
        const cornerRadius = 30
        this.guideBubbleView.drawTextureBgAroundContent(horizontalBuffer, verticalBuffer, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_section_dark), cornerRadius)
        this.guideBubbleView.y = this.guideBubbleViewPointer.y - this.guideBubbleView.height
        this.centerGuideTextView()
    }

    private updateGuideBubbleText() {
        if(this.guideTextView == undefined) this.drawGuideBubbleTextView()

        this.shownTranslationObject = this.getTranslationObjectForGuideBubble()
        this.guideTextView.setText(this.shownTranslationObject)
        this.drawBackgroundAroundText()
    }

    private getTranslationObjectForGuideBubble(): TranslationObject {
        if(this.isWelcomeAction()) return this.currentBeginnerModeAction.guideText
        if(this.uiGameManager.gameController.isMyTurn) return this.currentBeginnerModeAction.guideText
        const notYourTurnText: TranslationObject = this.getTextForWhenNotYourTurn()
        return notYourTurnText
    }

    private centerGuideTextView() {
        this.guideTextView.centerXInParent()
        this.guideTextView.y = (this.guideBubbleView.height - this.guideTextView.height) / 2
    }

    private hide() {
        if(this.guideBubbleView.parent != undefined) this.guideBubbleView.parent.removeChild(this.guideBubbleView)
        if(this.guideBubbleViewPointer.parent != undefined) this.guideBubbleViewPointer.parent.removeChild(this.guideBubbleViewPointer)
    }

    private show() {
        if(this.guideBubbleView.parent == undefined) this.parentContainer.addChild(this.guideBubbleView)
        if(this.guideBubbleViewPointer.parent == undefined) this.parentContainer.addChild(this.guideBubbleViewPointer)
    }

    private animate() {
        new UIGameFlickerAnimation(this.guideBubbleView).start()
        new UIGameFlickerAnimation(this.guideBubbleViewPointer).start()
    }

    private onTradeOfferReceived(tradeId: TradeId) {
        const tradeState = this.uiGameManager.gameController.tradeState.activeOffers[tradeId]
        if(tradeState == undefined) return

        const currentTurnPlayerColors = this.uiGameManager.gameController.currentStateValidator.currentTurnPlayers
        const playerStates = this.uiGameManager.gameController.getPlayerValidators()
        const embargoState = this.uiGameManager.gameController.tradeState.embargoState
        const tradeStateController = new TradeValidator(tradeState, currentTurnPlayerColors, playerStates, embargoState)

        this.ongoingTradeExists = true
        const shouldAcceptTrade = tradeState.forcedResponse == TradeResponseStatus.Accepted

        const receivingResources = tradeStateController.getReceivingResources(this.uiGameManager.gameController.myColor)
        const resourceName = EnumUtils.getEnumKey(receivingResources[0], CardEnum)
        this.receivingResourceName = shouldAcceptTrade ? resourceName : undefined
    }

    private getTextForWhenNotYourTurn(): TranslationObject {
        const notYourTurnText: TranslationObject = {key: 'strings:game.errors.notYourTurn'}

        const shouldAcceptTrade = this.receivingResourceName != undefined
        const translationForAcceptTrade: TranslationObject = {key: 'strings:game.beginnerMode.acceptTrade', options: {resource: this.receivingResourceName}}
        const translationForRejectTrade: TranslationObject = {key: 'strings:game.beginnerMode.doNotAcceptTrade'}

        const respondToTradeText: TranslationObject = shouldAcceptTrade ? translationForAcceptTrade : translationForRejectTrade
        return this.ongoingTradeExists ? respondToTradeText : notYourTurnText
    }

    destroy() {
        this.uiGameManager.eventController.unregisterUIGameMechanic(this)
        this.hide()
        this.guideBubbleView.destroy()
        this.guideBubbleViewPointer.destroy()
        this.guideTextView?.destroy()
    }
}
